import { useAuth } from '@warehouselogix/web-common';
import { useMemo } from 'react';

import { createAdminHttp } from './http';
import type { AdminHttp } from './http';

/**
 * The ONE place the admin console binds its HTTP function to MSAL: bearer
 * tokens come from `useAuth().acquireToken`; a 401 from the API starts the
 * sign-in redirect. Both {@link useAdminApi} and {@link usePlatformApi}
 * build on this so the two facades never drift in auth behaviour.
 *
 * `onForbidden` is optional and referentially significant — pass a stable
 * callback (useCallback) or the facade re-memoizes every render.
 */
export function useAdminHttp(onForbidden?: (message: string) => void): AdminHttp {
  const { acquireToken, login } = useAuth();

  return useMemo<AdminHttp>(
    () =>
      createAdminHttp({
        baseUrl: import.meta.env.VITE_API_BASE,
        getToken: acquireToken,
        onUnauthorized: () => {
          // MSAL redirect navigates away; the in-flight error is never shown.
          void login();
        },
        ...(onForbidden !== undefined ? { onForbidden } : {}),
      }),
    [acquireToken, login, onForbidden]
  );
}
